import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdminStudentPlatformHeaderComponent } from '@edtech-ui/admin-student-platform/admin-student-platform-header/admin-student-platform-header.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import * as XLSX from 'xlsx';
import { Router } from '@angular/router';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';

@Component({
  selector: 'app-superadmin-all-candidate',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminStudentPlatformHeaderComponent, MatTooltipModule, MatProgressSpinnerModule,
    HasPermissionDirective
  ],
  templateUrl: './superadmin-all-candidate.component.html',
  styleUrl: './superadmin-all-candidate.component.scss'
})
export class SuperadminAllCandidateComponent implements OnInit, OnDestroy {

  // Candidates State
  candidatesData: any[] = [];
  allCandidatesData: any[] = [];
  isLoading = false;
  activeTab = '';
  companyMap: { [id: string]: string } = {};
  total = 0;
  page = 1;
  limit = 10;
  totalPages = 1;
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  searchTerm = '';
  selectedCompany = 'All Companies';
  selectedJob = 'All Jobs';
  selectedStatus = 'All Status';
  selectedDate = '';
  searchSubscription = new Subscription();
  searchSubject = new Subject<string>();

  companies: string[] = ['All Companies'];
  jobs: string[] = ['All Jobs'];
  candidateStatuses = [
    { value: 'All Status', label: 'All Status' },
    { value: 'new', label: 'New' },
    { value: 'applied', label: 'Applied' },
    { value: 'assessment_pending', label: 'Assessment Pending' },
    { value: 'assessment_completed', label: 'Assessment Completed' },
    { value: 'ai_interview_pending', label: 'AI Interview Pending' },
    { value: 'ai_interview_done', label: 'AI Interview Completed' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'selected', label: 'Selected' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'mock_done', label: 'Mock Done' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'hired', label: 'Hired' }
  ];

  PermissionForStudents = PermissionForStudents;
  canViewFresher = false;
  canViewExperienced = false;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private router: Router,
    private companyService: CompanyService,
    private _authService: AuthServiceService
  ) { }

  ngOnInit(): void {
    this.canViewFresher = this._authService.hasPermission(PermissionForStudents.CANDIDATE_VIEW_FRESHER);
    this.canViewExperienced = this._authService.hasPermission(PermissionForStudents.CANDIDATE_VIEW_EXPERIENCED);

    if (this.canViewFresher) {
      this.activeTab = 'fresher';
    } else if (this.canViewExperienced) {
      this.activeTab = 'experienced';
    }
    this.searchSubscription.add(
      this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
        this.page = 1;
        this.getCandidates();
      })
    );

    if (this.canViewFresher || this.canViewExperienced) {
    this.loadCompanies();
    this.loadFilterSuggestions();
    this.getCandidates();
    } 
  }

  loadCompanies(): void {
    this.fetchCompaniesPage(1);
  }

  fetchCompaniesPage(page: number): void {
    this.companyService.getAllCompaniesList({ page, limit: 100 }).subscribe({
      next: (res) => {
        if (res.Success && res.Data && res.Data.companies) {
          res.Data.companies.forEach(c => {
            if (c.company_id && c.company_name) {
              this.companyMap[c.company_id] = c.company_name;
            }
          });
          this.enrichCandidatesWithCompanyNames();

          const totalCompanies = res.Data.total || 0;
          const loadedCompaniesCount = page * 100;
          if (loadedCompaniesCount < totalCompanies) {
            this.fetchCompaniesPage(page + 1);
          }
        }
      }
    });
  }

  loadFilterSuggestions(): void {

	   if ( (this.activeTab === 'fresher' && !this.canViewFresher) ||
		    (this.activeTab === 'experienced' && !this.canViewExperienced) ||!this.activeTab ) {
		    return;
		}
    let params = new HttpParams()
      .set('page', '1')
      .set('limit', '100')
      .set('candidate_type', this.activeTab);

    this.http.get<any>(`${environment.candidateApiUrl}/admin/list`, { params }).subscribe({
      next: (response) => {
        if (response.Success || response.success) {
          const dataObj = response.Data || response.data || {};
          const candidates = dataObj.candidates || [];

          const uniqueCompanies = Array.from(
            new Set(candidates.map((c: any) => c.company_name || this.companyMap[c.company_id]).filter(Boolean))
          ).sort();
          this.companies = ['All Companies', ...uniqueCompanies] as string[];

          const uniqueJobs = Array.from(
            new Set(candidates.map((c: any) => c.job_title).filter(Boolean))
          ).sort();
          this.jobs = ['All Jobs', ...uniqueJobs] as string[];
        }
      }
    });
  }

  enrichCandidatesWithCompanyNames(): void {
    this.candidatesData = this.candidatesData.map(c => ({
      ...c,
      company_name: c.company_name || this.companyMap[c.company_id] || 'Direct Candidate'
    }));
  }

  getStatusClass(status: string): string {
    if (!status) return 'pending';
    const s = status.toLowerCase().trim();
    if (['approved', 'selected', 'shortlisted', 'hired', 'assessment_completed', 'mock_done', 'ai_interview_done'].includes(s)) {
      return 'approved';
    }
    if (s === 'rejected') {
      return 'rejected';
    }
    return 'pending'; 
  }

  getCandidates(): void {

		if  ((this.activeTab === 'fresher' && !this.canViewFresher) ||
			   (this.activeTab === 'experienced' && !this.canViewExperienced) ||!this.activeTab) {
			this.candidatesData = [];
			this.total = 0;
			this.totalPages = 1;
			return;
		}
    this.isLoading = true;

    let params = new HttpParams()
      .set('page', this.page.toString())
      .set('limit', this.limit.toString())
      .set('candidate_type', this.activeTab); 

    if (this.searchTerm?.trim()) {
      params = params.set('search', this.searchTerm.trim());
    }

    if (this.selectedStatus !== 'All Status') {
      params = params.set('status', this.selectedStatus.toLowerCase());
    }

    if (this.selectedCompany !== 'All Companies') {
      params = params.set('company_name', this.selectedCompany);
    }

    if (this.selectedJob !== 'All Jobs') {
      params = params.set('job_title', this.selectedJob);
    }

    if (this.selectedDate) {
      params = params.set('created_at', this.selectedDate);
    }

    this.http.get<any>(`${environment.candidateApiUrl}/admin/list`, { params }).pipe(
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe({
      next: (response) => {
        if (response.Success || response.success) {
          const dataObj = response.Data || response.data || {};

          this.total = dataObj.total ?? 0;
          this.page = dataObj.page ?? 1;
          this.limit = dataObj.limit ?? 10;
          this.totalPages = dataObj.total_pages ?? 1;


          this.candidatesData = dataObj.candidates || dataObj.data || [];
          this.enrichCandidatesWithCompanyNames();
        } else {
          this.toastr.error(response.Message || 'Failed to retrieve candidates');
        }
      },
      error: (err) => {
        this.toastr.error('Error fetching candidate details');
      }
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.getCandidates();
  }

  clearDateFilter(): void {
    this.selectedDate = '';
    this.page = 1;
    this.getCandidates();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedCompany = 'All Companies';
    this.selectedJob = 'All Jobs';
    this.selectedStatus = 'All Status';
    this.selectedDate = '';
    this.page = 1;
    this.getCandidates();
  }

  setTab(tab: 'fresher' | 'experienced'): void {
	    if (tab === 'fresher' && !this.canViewFresher) return;
      if (tab === 'experienced' && !this.canViewExperienced) return;
    this.activeTab = tab;
    this.page = 1;
    this.loadFilterSuggestions();
    this.getCandidates();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm?.trim() || '');
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.getCandidates();
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.getCandidates();
    }
  }

  downloadResume(resumeUrl: string): void {
    if (!resumeUrl) {
      this.toastr.warning('Resume file is not available');
      return;
    }
    window.open(resumeUrl, '_blank');
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.candidatesData.sort((a: any, b: any) => {
      let valueA = a[field];
      let valueB = b[field];

      if (valueA == null) valueA = '';
      if (valueB == null) valueB = '';

      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }

  exportToExcel(): void {
    if (this.candidatesData.length === 0) {
      this.toastr.warning('No data to export');
      return;
    }
    const exportData = this.candidatesData.map(c => ({
      'Name': c.name,
      'Email': c.email,
      'Mobile': c.mobile,
      'Role': c.job_title || c.role || '',
      'Company': c.company_name || 'Direct Candidate',
      'Location': c.current_location || c.city_pref_1 || '',
      'Status': c.status,
      'Applied Date': c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Candidates': worksheet },
      SheetNames: ['Candidates']
    };

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `candidates_${new Date().toISOString().slice(0, 10)}.xlsx`);
    this.toastr.success('Excel exported successfully');
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
   
 